/**
 * Diagnostics — read-only production data shape, for decisions that cannot be
 * made against preview data.
 *
 * Exists because the API is Bearer-token authenticated, so pasting a
 * diagnostic URL into a browser tab returns "Not authenticated". This page
 * runs the same request from inside the app, where the token is available, and
 * lets an admin copy or download the JSON.
 *
 * Each report states the decision it exists to answer and when it should be
 * deleted. Reports here are STRICTLY read-only and carry no PII.
 */
import React, { useState } from 'react';
import { diagnosticsAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import AccessState from '../components/auth/AccessState';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Play, Copy, Download, Stethoscope } from 'lucide-react';

const REPORTS = [
  {
    key: 'data-inventory',
    label: 'Data inventory & import history',
    purpose: 'What has actually been loaded — row counts per collection, how many jobs carry a '
      + 'rate line, and which imports have been run. Counts only, no names.',
    retire: 'Keep — this is the general "what is in here" report.',
    run: () => diagnosticsAPI.dataInventory(),
  },
];

export default function Diagnostics() {
  const canView = useCap('admin', 'view');
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(null);

  const run = async (report) => {
    setRunning(report.key);
    setOutput(null);
    try {
      const { data } = await report.run();
      setOutput({ key: report.key, json: JSON.stringify(data, null, 2) });
      toast.success('Report ready — copy or download it below');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report failed');
    } finally { setRunning(null); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output.json);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — use Download instead');
    }
  };

  const download = () => {
    const blob = new Blob([output.json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${output.key}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="diagnostics-page">
        <AccessState module="admin" action="view" items={[]} denied
          noAccessTitle="No access to Diagnostics"
          noAccessDescription="Diagnostic reports are administration-only." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Stethoscope className="w-7 h-7 text-slate-400" />Diagnostics
        </h1>
        <p className="text-base text-fg-muted mt-1 max-w-3xl">
          Read-only reports on the shape of live data, for questions that cannot be answered
          against preview. Nothing here writes to the database and no report includes names or
          other personal details — records are identified by id only.
        </p>
      </div>

      {REPORTS.map(r => (
        <Card key={r.key} data-testid={`diagnostic-${r.key}`}>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-medium">{r.label}</h2>
                  <Badge variant="outline" className="text-xs">read-only</Badge>
                </div>
                <p className="text-sm text-fg-muted mt-1 max-w-2xl">{r.purpose}</p>
                <p className="text-xs text-fg-muted mt-1">{r.retire}</p>
              </div>
              <Button onClick={() => run(r)} disabled={running === r.key} className="shrink-0"
                data-testid={`diagnostic-run-${r.key}`}>
                <Play className="w-4 h-4 mr-2" />{running === r.key ? 'Running…' : 'Run'}
              </Button>
            </div>

            {output?.key === r.key && (
              <div className="space-y-2 pt-2 border-t border-line-subtle">
                <div className="flex items-center gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={copy}
                    data-testid="diagnostic-copy"><Copy className="w-4 h-4 mr-2" />Copy JSON</Button>
                  <Button size="sm" variant="outline" onClick={download}
                    data-testid="diagnostic-download"><Download className="w-4 h-4 mr-2" />Download</Button>
                </div>
                <pre className="text-xs bg-slate-950 text-slate-100 rounded-md p-4 overflow-auto max-h-[520px]"
                  data-testid="diagnostic-output">{output.json}</pre>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
